import { Backdrop, Box, Button, CircularProgress, Dialog, Skeleton, Typography } from '@mui/material';
import { Check, MessageSquarePlus, Trash2, ArrowLeft } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { httpClient } from '../../../common/httpclient';
import { GroupLabel, KitButton, KitCard } from '../../../component/common/MobileKit';
import {
    KitDropzone,
    KitFormBar,
    KitProgress,
    KitTextarea,
} from '../../../component/common/MobileKitExtras';
import { protoIcon } from '../../../component/common/ProtoIcon';
import SaveProgressDialog from './SaveProgressDialog';
import { appBarButtonSx } from '../../../component/layout/AppBarActions';

// ============================================================================
// PREENCHIMENTO DE CHECKLIST DA OS — shell mobile.
//
// Layout de `prototipos-mobile/checklists/preencher.html`: cartão de progresso
// (veículo · OS + respondidos/total + barra), um cartão por grupo (rótulo +
// linhas com os botões de resposta em ícone: OK ✓ / NOK ✕ / Manutenção −),
// "Anexar fotos" e rodapé fixo. `MobileFillView` é compartilhado entre o
// preenchimento avulso (`Filler.js`, rota `/checklist/:id/fill`) e o modal da
// OS (`ChecklistFillerModal.js` → `MobileChecklistFiller`, em tela cheia).
//
// Ao salvar, o app faz `POST /checklist/fill` e depois `POST
// /checklist/service-order/pdf` com as fotos (base64) JÁ COM as marcações de
// avaria desenhadas na imagem (canvas) — como o `fv-app`.
//
// Os dados (grupos/avaliações) e o submit vêm de quem usa; este arquivo só
// cuida da apresentação mobile, das fotos e das marcações.
// ============================================================================

const CheckIcon = protoIcon('check');
const XIcon = protoIcon('x');
const MinusIcon = protoIcon('minus');
const CameraIcon = protoIcon('camera');

// Colors.blueAccent do Flutter — mesmo accent do MobileLayout.
const MOBILE_ACCENT = '#448AFF';
const DAMAGE_COLOR = '#F44336';
// Lado maior máximo da foto enviada (fotos de celular têm 12MP+; o PDF mostra
// a imagem pequena e o corpo da requisição é JSON/base64).
const MAX_PHOTO_SIDE = 1600;
const MARK_SIZE = 34;

const SAFE_TOP = 'env(safe-area-inset-top, 0px)';
const SAFE_BOTTOM = 'env(safe-area-inset-bottom, 0px)';

function classifyFeedback(name) {
    const upper = String(name || '').toUpperCase();
    if (upper === 'OK') return 'ok';
    if (upper === 'NOK' || upper.includes('ATEN')) return 'nok';
    if (upper.includes('MANUTEN')) return 'manut';
    return 'other';
}

const DEFAULT_FEEDBACK_OPTIONS = [
    { feedback_id: 'ok', feedback_name: 'OK', feedback_order: 1 },
    { feedback_id: 'nok', feedback_name: 'NOK', feedback_order: 2 },
    { feedback_id: 'manutencao', feedback_name: 'Manutenção', feedback_order: 3 },
];

const byFeedbackOrder = (a, b) => (a.feedback_order || 0) - (b.feedback_order || 0);

// Opções de resposta do grupo; sem opções próprias herda as do primeiro grupo que
// tiver; sem nenhuma, usa o padrão (mesma regra do preenchimento desktop).
function resolveFeedbackOptions(group, groups) {
    const active = (g) => (Array.isArray(g.group_feedback)
        ? g.group_feedback.filter((f) => f.active !== false).sort(byFeedbackOrder)
        : []);
    const own = active(group);
    if (own.length > 0) return own;
    for (const other of groups) {
        const fromOther = active(other);
        if (fromOther.length > 0) return fromOther;
    }
    return DEFAULT_FEEDBACK_OPTIONS;
}

function resolveInspectionItems(group) {
    return Array.isArray(group.group_inspection_items)
        ? group.group_inspection_items
            .filter((i) => i.active !== false)
            .sort((a, b) => (a.insp_item_order || 0) - (b.insp_item_order || 0))
        : [];
}

const clamp01 = (n) => Math.min(1, Math.max(0, n));

// Desenha a foto (reduzida) com as marcações numeradas e devolve o base64 JPEG
// (sem prefixo `data:`), que é o formato que `POST /checklist/service-order/pdf`
// espera em `images`. Marcações ficam em coordenadas normalizadas (0..1), então
// a escala da imagem exibida não importa.
export function renderPhotoBase64(photo) {
    return new Promise((resolve, reject) => {
        const img = new window.Image();
        img.onload = () => {
            const scale = Math.min(1, MAX_PHOTO_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
            const w = Math.max(1, Math.round(img.naturalWidth * scale));
            const h = Math.max(1, Math.round(img.naturalHeight * scale));
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Canvas indisponível'));
                return;
            }
            ctx.drawImage(img, 0, 0, w, h);

            const radius = Math.max(16, Math.round(Math.min(w, h) * 0.035));
            photo.marks.forEach((mark, index) => {
                const cx = mark.x * w;
                const cy = mark.y * h;
                ctx.beginPath();
                ctx.arc(cx, cy, radius, 0, Math.PI * 2);
                ctx.fillStyle = 'rgba(244,67,54,0.3)';
                ctx.fill();
                ctx.lineWidth = Math.max(3, radius / 6);
                ctx.strokeStyle = DAMAGE_COLOR;
                ctx.stroke();
                ctx.font = `bold ${Math.round(radius * 1.1)}px sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.lineWidth = 4;
                ctx.strokeStyle = '#fff';
                ctx.strokeText(String(index + 1), cx, cy);
                ctx.fillStyle = DAMAGE_COLOR;
                ctx.fillText(String(index + 1), cx, cy);
            });

            resolve(canvas.toDataURL('image/jpeg', 0.85).split(',')[1]);
        };
        img.onerror = () => reject(new Error('Não foi possível ler a foto'));
        img.src = photo.url;
    });
}

// Marcador numerado de avaria (círculo vermelho translúcido, como no app).
function MarkerBadge({ number, selected, size = MARK_SIZE }) {
    return (
        <Box
            sx={{
                width: size,
                height: size,
                borderRadius: '50%',
                boxSizing: 'border-box',
                border: '3px solid',
                borderColor: DAMAGE_COLOR,
                backgroundColor: 'rgba(244,67,54,0.3)',
                color: DAMAGE_COLOR,
                fontWeight: 800,
                fontSize: size >= 30 ? 13 : 9,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textShadow: '0 0 3px #fff, 0 0 3px #fff',
                boxShadow: selected ? `0 0 0 3px ${MOBILE_ACCENT}` : 'none',
            }}
        >
            {number}
        </Box>
    );
}

// Tela de marcação de avarias (DamageMarkingScreen do app): toque na foto coloca
// um marcador numerado; arraste para mover; toque no marcador para selecioná-lo
// e remover.
function DamageMarkingDialog({ photo, onCancel, onSave }) {
    const [marks, setMarks] = useState(photo.marks);
    const [selected, setSelected] = useState(null);
    const wrapRef = useRef(null);
    const dragRef = useRef(null);

    const toRatio = (clientX, clientY) => {
        const rect = wrapRef.current.getBoundingClientRect();
        return {
            x: clamp01((clientX - rect.left) / rect.width),
            y: clamp01((clientY - rect.top) / rect.height),
        };
    };

    const handleImageClick = (e) => {
        const point = toRatio(e.clientX, e.clientY);
        const next = [...marks, point];
        setMarks(next);
        setSelected(next.length - 1);
    };

    const removeSelected = () => {
        if (selected === null) return;
        setMarks((prev) => prev.filter((_, idx) => idx !== selected));
        setSelected(null);
    };

    const clearAll = () => {
        if (window.confirm('Limpar todas as marcações? Esta ação não pode ser desfeita.')) {
            setMarks([]);
            setSelected(null);
        }
    };

    return (
        <Dialog open fullScreen onClose={onCancel} sx={{ zIndex: (t) => t.zIndex.modal + 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#111' }}>
                <Box
                    sx={{
                        pt: SAFE_TOP, pl: 'calc(4px + env(safe-area-inset-left, 0px))', pr: 'calc(12px + env(safe-area-inset-right, 0px))',
                        height: `calc(56px + ${SAFE_TOP})`, boxSizing: 'border-box', flexShrink: 0,
                        display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: '#FB8C00', color: '#fff',
                    }}
                >
                    <Box component="button" type="button" onClick={onCancel} aria-label="Cancelar marcação"
                        sx={{ width: 44, height: 44, border: 'none', background: 'transparent', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <ArrowLeft size={22} />
                    </Box>
                    <Typography component="h2" sx={{ flex: 1, fontSize: 18, fontWeight: 700, color: '#fff' }}>
                        Marcar avarias
                    </Typography>
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, overflow: 'hidden' }}>
                    <Box ref={wrapRef} sx={{ position: 'relative', display: 'inline-block', lineHeight: 0, maxWidth: '100%' }}>
                        <Box
                            component="img"
                            src={photo.url}
                            alt="Foto do veículo"
                            draggable={false}
                            onClick={handleImageClick}
                            sx={{
                                display: 'block', maxWidth: '100%',
                                maxHeight: `calc(100vh - 56px - 150px - ${SAFE_TOP} - ${SAFE_BOTTOM})`,
                                '@supports (height: 100dvh)': { maxHeight: `calc(100dvh - 56px - 150px - ${SAFE_TOP} - ${SAFE_BOTTOM})` },
                                userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', touchAction: 'manipulation',
                            }}
                        />
                        {marks.map((mark, index) => (
                            <Box
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                role="button"
                                aria-label={`Marcação ${index + 1}`}
                                onPointerDown={(e) => {
                                    e.stopPropagation();
                                    e.currentTarget.setPointerCapture(e.pointerId);
                                    dragRef.current = index;
                                    setSelected(index);
                                }}
                                onPointerMove={(e) => {
                                    if (dragRef.current !== index) return;
                                    const point = toRatio(e.clientX, e.clientY);
                                    setMarks((prev) => prev.map((m, idx) => (idx === index ? point : m)));
                                }}
                                onPointerUp={() => { dragRef.current = null; }}
                                onPointerCancel={() => { dragRef.current = null; }}
                                sx={{
                                    position: 'absolute', left: `${mark.x * 100}%`, top: `${mark.y * 100}%`,
                                    transform: 'translate(-50%, -50%)', touchAction: 'none', cursor: 'grab',
                                    // Área de toque >= 44px ao redor do círculo visual.
                                    width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                            >
                                <MarkerBadge number={index + 1} selected={selected === index} />
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box
                    sx={{
                        flexShrink: 0, backgroundColor: 'background.paper', p: 1.5,
                        pb: `calc(12px + ${SAFE_BOTTOM})`, pl: 'calc(12px + env(safe-area-inset-left, 0px))', pr: 'calc(12px + env(safe-area-inset-right, 0px))',
                        display: 'flex', flexDirection: 'column', gap: 1,
                    }}
                >
                    {selected !== null ? (
                        <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            startIcon={<Trash2 size={16} />}
                            onClick={removeSelected}
                            sx={{ minHeight: 44 }}
                        >
                            Remover marcação {selected + 1}
                        </Button>
                    ) : (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center', minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            Toque na foto para marcar uma avaria. Arraste para mover.
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {marks.length > 0 && (
                            <Button variant="outlined" onClick={clearAll} sx={{ minHeight: 48, flexShrink: 0 }}>
                                Limpar
                            </Button>
                        )}
                        <Button variant="outlined" onClick={onCancel} sx={{ minHeight: 48, flex: 1 }}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Check size={18} />}
                            onClick={() => onSave(marks)}
                            sx={{ minHeight: 48, flex: 2, backgroundColor: '#FB8C00', '&:hover': { backgroundColor: '#EF6C00' } }}
                        >
                            Salvar ({marks.length})
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
}


// Miniaturas das fotos anexadas (toque abre a marcação de avarias).
function PhotoThumbs({ photos, onEdit, onDelete }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px', mt: '12px' }}>
            {photos.map((photo) => (
                <Box key={photo.id} sx={{ position: 'relative', borderRadius: '16px', overflow: 'hidden', border: '1px solid', borderColor: 'divider', aspectRatio: '1 / 1', backgroundColor: 'background.subtle' }}>
                    <Box
                        component="button"
                        type="button"
                        onClick={() => onEdit(photo.id)}
                        aria-label="Marcar avarias na foto"
                        sx={{ p: 0, border: 'none', background: 'transparent', width: '100%', height: '100%', cursor: 'pointer', display: 'block', position: 'relative' }}
                    >
                        <Box component="img" src={photo.url} alt="Foto do veículo" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        {photo.marks.map((mark, index) => (
                            <Box
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                sx={{ position: 'absolute', left: `${mark.x * 100}%`, top: `${mark.y * 100}%`, transform: 'translate(-50%, -50%)' }}
                            >
                                <MarkerBadge number={index + 1} size={20} />
                            </Box>
                        ))}
                    </Box>
                    {photo.marks.length > 0 && (
                        <Box sx={{ position: 'absolute', left: 6, bottom: 6, px: '8px', py: '3px', borderRadius: '12px', backgroundColor: DAMAGE_COLOR, color: '#fff', fontSize: 11, fontWeight: 700 }}>
                            {photo.marks.length} {photo.marks.length === 1 ? 'avaria' : 'avarias'}
                        </Box>
                    )}
                    <Box
                        component="button"
                        type="button"
                        onClick={() => onDelete(photo.id)}
                        aria-label="Remover foto"
                        sx={{ position: 'absolute', top: 2, right: 2, width: 44, height: 44, p: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <Box sx={{ width: 30, height: 30, borderRadius: '50%', backgroundColor: DAMAGE_COLOR, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Trash2 size={15} />
                        </Box>
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

// `.check-line .tri button` — 38x38, raio 12, borda 1.5px; ativo no tom da resposta
// (OK = ✓ verde, NOK = ✕ vermelho, Manutenção = − âmbar).
const TRI_META = {
    ok: { Icon: CheckIcon, tone: 'success' },
    nok: { Icon: XIcon, tone: 'error' },
    manut: { Icon: MinusIcon, tone: 'warning' },
};

function TriButton({ feedback, selected, onClick }) {
    const kind = classifyFeedback(feedback.feedback_name);
    const meta = TRI_META[kind];
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            aria-pressed={selected}
            aria-label={feedback.feedback_name}
            title={feedback.feedback_name}
            sx={(theme) => {
                const tone = meta ? theme.palette[meta.tone] : theme.palette.primary;
                const toneBg = meta ? tone.bg : theme.palette.info.bg;
                return {
                    minWidth: 38,
                    height: 38,
                    p: meta ? 0 : '0 10px',
                    borderRadius: '12px',
                    border: '1.5px solid',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'inherit',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                    ...(selected
                        ? {
                            backgroundColor: toneBg,
                            borderColor: tone.main,
                            color: meta && meta.tone === 'warning' && theme.palette.mode === 'light' ? '#b45309' : tone.main,
                        }
                        : { backgroundColor: theme.palette.background.paper, borderColor: theme.palette.divider, color: theme.palette.text.disabled }),
                };
            }}
        >
            {meta ? <meta.Icon size={18} /> : feedback.feedback_name}
        </Box>
    );
}

// `.check-line` — nome do item (14/700) + botões de resposta; a observação opcional abre ao tocar no nome.
function CheckLine({ item, feedbackOptions, evaluation, onAnswer, onObservation }) {
    const [obsOpen, setObsOpen] = useState(Boolean(evaluation.observation));
    return (
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '13px 0' }} data-item-id={item.insp_item_id}>
                <Box
                    component="button"
                    type="button"
                    onClick={() => setObsOpen((prev) => !prev)}
                    aria-expanded={obsOpen}
                    aria-label={`Observação de ${item.insp_item_name}`}
                    sx={{ flex: 1, minWidth: 0, p: 0, border: 0, background: 'none', textAlign: 'left', fontFamily: 'inherit', fontWeight: 700, fontSize: 14, lineHeight: 1.4, color: 'text.primary', cursor: 'pointer' }}
                >
                    {item.insp_item_name}
                    <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle', ml: '6px', color: evaluation.observation ? 'primary.main' : 'text.disabled' }}>
                        <MessageSquarePlus size={14} />
                    </Box>
                </Box>
                <Box role="group" aria-label={`Resposta para ${item.insp_item_name}`} sx={{ display: 'flex', gap: '6px', ml: 'auto' }}>
                    {feedbackOptions.map((feedback) => (
                        <TriButton
                            key={feedback.feedback_id}
                            feedback={feedback}
                            selected={evaluation.answer_id === feedback.feedback_id}
                            onClick={() => {
                                // Reclicar na resposta atual não desmarca (mesma regra do desktop).
                                if (evaluation.answer_id !== feedback.feedback_id) {
                                    onAnswer(item.insp_item_id, feedback.feedback_id);
                                }
                            }}
                        />
                    ))}
                </Box>
            </Box>
            {obsOpen && (
                <Box sx={{ pb: '13px' }}>
                    <KitTextarea
                        rows={2}
                        placeholder="Observações (opcional)"
                        aria-label={`Observação de ${item.insp_item_name}`}
                        value={evaluation.observation || ''}
                        onChange={(e) => onObservation(item.insp_item_id, e.target.value)}
                        sx={{ minHeight: 72 }}
                    />
                </Box>
            )}
        </Box>
    );
}

/**
 * Corpo do preenchimento mobile (cartão de progresso, grupos, fotos e rodapé fixo).
 * Só apresentação: respostas, observações e o envio vêm de quem usa (`onSubmit(photos)`).
 */
export function MobileFillView({
    loading,
    serviceOrder,
    groups,
    evaluations,
    onAnswer,
    onObservation,
    onSubmit,
    submitting,
    saveProgress,
    canFill = true,
    emptyText = 'Nenhum grupo encontrado neste checklist',
    warning,
    onDirtyChange,
}) {
    const [photos, setPhotos] = useState([]);
    const [markingId, setMarkingId] = useState(null);
    const photosRef = useRef(photos);
    photosRef.current = photos;
    const pickerRef = useRef(null);

    // Libera as object URLs das fotos ao desmontar (fechar a tela).
    useEffect(() => () => {
        photosRef.current.forEach((p) => URL.revokeObjectURL(p.url));
    }, []);

    const groupsView = useMemo(() => groups.map((group) => ({
        group,
        items: resolveInspectionItems(group),
        feedbackOptions: resolveFeedbackOptions(group, groups),
    })), [groups]);

    const progress = useMemo(() => {
        let total = 0;
        let answered = 0;
        let firstPendingId = null;
        groupsView.forEach(({ items }) => {
            items.forEach((item) => {
                total += 1;
                if (evaluations[item.insp_item_id]?.answer_id) {
                    answered += 1;
                } else if (!firstPendingId) {
                    firstPendingId = item.insp_item_id;
                }
            });
        });
        return { total, answered, firstPendingId, pct: total > 0 ? Math.round((answered / total) * 100) : 0 };
    }, [groupsView, evaluations]);

    useEffect(() => {
        if (onDirtyChange) onDirtyChange(progress.answered > 0 || photos.length > 0);
    }, [progress.answered, photos.length, onDirtyChange]);

    const handlePicked = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        const photo = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, url: URL.createObjectURL(file), marks: [] };
        setPhotos((prev) => [...prev, photo]);
        // Como no app: ao tirar/escolher a foto, abre direto a marcação de avarias.
        setMarkingId(photo.id);
    };

    const handleDeletePhoto = (photoId) => {
        const target = photos.find((p) => p.id === photoId);
        if (target) URL.revokeObjectURL(target.url);
        setPhotos((prev) => prev.filter((p) => p.id !== photoId));
    };

    const handleSaveMarks = (marks) => {
        setPhotos((prev) => prev.map((p) => (p.id === markingId ? { ...p, marks } : p)));
        setMarkingId(null);
    };

    const goToPending = () => {
        if (!progress.firstPendingId) return;
        const el = document.querySelector(`[data-item-id="${progress.firstPendingId}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const markingPhoto = photos.find((p) => p.id === markingId);
    const orderNumber = serviceOrder ? (serviceOrder.number || serviceOrder.id) : '';
    const headline = [serviceOrder?.vehicle_name, orderNumber ? `OS #${orderNumber}` : ''].filter(Boolean).join(' · ') || 'Checklist';

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            {warning && <Box sx={{ mb: '12px' }}>{warning}</Box>}

            {loading ? (
                <>
                    <Skeleton variant="rounded" height={72} sx={{ borderRadius: '16px' }} />
                    <Skeleton variant="rounded" height={190} sx={{ borderRadius: '16px', mt: '24px' }} />
                </>
            ) : (
                <>
                    <KitCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                            <Box sx={{ fontWeight: 700, minWidth: 0 }}>{headline}</Box>
                            <Box component="span" sx={{ fontSize: 12.5, fontWeight: 700, flexShrink: 0 }}>{progress.answered}/{progress.total}</Box>
                        </Box>
                        <KitProgress value={progress.pct} sx={{ mt: '8px' }} />
                    </KitCard>

                    {groupsView.length === 0 && (
                        <Typography sx={{ textAlign: 'center', color: 'text.secondary', py: 4, fontSize: 14 }}>{emptyText}</Typography>
                    )}

                    {groupsView.map(({ group, items, feedbackOptions }) => {
                        if (items.length === 0) return null;
                        return (
                            <React.Fragment key={group.group_id}>
                                <GroupLabel>{group.group_name}</GroupLabel>
                                <KitCard sx={{ p: '4px 14px' }}>
                                    {items.map((item) => (
                                        <CheckLine
                                            key={item.insp_item_id}
                                            item={item}
                                            feedbackOptions={feedbackOptions}
                                            evaluation={evaluations[item.insp_item_id] || {}}
                                            onAnswer={onAnswer}
                                            onObservation={onObservation}
                                        />
                                    ))}
                                </KitCard>
                            </React.Fragment>
                        );
                    })}

                    <GroupLabel>Fotos do veículo</GroupLabel>
                    {/* Sem `capture`: no celular o seletor nativo oferece câmera ou galeria. */}
                    <input ref={pickerRef} type="file" accept="image/*" hidden onChange={handlePicked} />
                    <KitDropzone icon={CameraIcon} iconSize={24} height={84} onClick={() => pickerRef.current && pickerRef.current.click()} aria-label="Anexar fotos">
                        Anexar fotos
                    </KitDropzone>
                    {photos.length > 0 && <PhotoThumbs photos={photos} onEdit={setMarkingId} onDelete={handleDeletePhoto} />}
                </>
            )}

            <KitFormBar>
                {progress.firstPendingId && !loading && (
                    <KitButton onClick={goToPending}>Pendente</KitButton>
                )}
                <KitButton
                    variant="primary"
                    onClick={() => onSubmit(photos)}
                    disabled={submitting || !canFill || loading || progress.answered === 0}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {submitting ? 'Salvando...' : 'Concluir'}
                </KitButton>
            </KitFormBar>

            {/* Bloqueia toques enquanto salva (checklist + geração do PDF). */}
            {/* Com `saveProgress` (modal da OS): % determinada; sem ele (Filler.js): spinner. */}
            {saveProgress === undefined ? (
                <Backdrop open={!!submitting} sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: '#fff' }}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            ) : (
                <SaveProgressDialog open={!!submitting} progress={saveProgress} />
            )}

            {markingPhoto && (
                <DamageMarkingDialog
                    key={markingPhoto.id}
                    photo={markingPhoto}
                    onCancel={() => setMarkingId(null)}
                    onSave={handleSaveMarks}
                />
            )}
        </Box>
    );
}

// Nome do template para o título (a estrutura `/checklist/{id}/view` não traz o nome).
export function useChecklistName(checklistId) {
    const [name, setName] = useState('');
    useEffect(() => {
        let active = true;
        if (!checklistId) return undefined;
        httpClient.get(`/checklist/${checklistId}`)
            .then((res) => { if (active) setName(res.data?.name || ''); })
            .catch(() => {});
        return () => { active = false; };
    }, [checklistId]);
    return name;
}

// Preenchimento da OS em tela cheia (shell mobile): barra superior do protótipo
// (voltar + título + subtítulo `OS #n`) + `MobileFillView`.
export default function MobileChecklistFiller({
    loading,
    checklistId,
    serviceOrder,
    groups,
    evaluations,
    onAnswer,
    onObservation,
    onSubmit,
    onClose,
    submitting,
    saveProgress,
    canFill,
}) {
    const [dirty, setDirty] = useState(false);
    const checklistName = useChecklistName(checklistId);
    const orderNumber = serviceOrder ? (serviceOrder.number || serviceOrder.id) : '';

    const handleClose = () => {
        if (submitting) return;
        if (!dirty || window.confirm('Sair sem salvar? O preenchimento será descartado.')) {
            onClose();
        }
    };
    const handleDirtyChange = useCallback((value) => setDirty(value), []);

    return (
        <Dialog open fullScreen onClose={handleClose}>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', backgroundColor: 'background.default' }}>
                <Box
                    component="header"
                    sx={{
                        flexShrink: 0,
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        p: 'calc(10px + env(safe-area-inset-top, 0px)) calc(12px + env(safe-area-inset-right, 0px)) 10px calc(12px + env(safe-area-inset-left, 0px))',
                        minHeight: 56,
                        backgroundColor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Box component="button" type="button" onClick={handleClose} aria-label="Voltar" disabled={submitting} sx={appBarButtonSx}>
                        <ArrowLeft size={20} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography component="h1" sx={{ fontSize: 17, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {checklistName || 'Preencher checklist'}
                        </Typography>
                        {orderNumber && <Box sx={{ fontSize: 11.5, color: 'text.disabled', mt: '1px', lineHeight: 1.4 }}>OS #{orderNumber}</Box>}
                    </Box>
                </Box>
                <Box sx={{ flex: 1, p: '16px calc(16px + env(safe-area-inset-right, 0px)) 16px calc(16px + env(safe-area-inset-left, 0px))' }}>
                    <MobileFillView
                        loading={loading}
                        serviceOrder={serviceOrder}
                        groups={groups}
                        evaluations={evaluations}
                        onAnswer={onAnswer}
                        onObservation={onObservation}
                        onSubmit={onSubmit}
                        submitting={submitting}
                        saveProgress={saveProgress}
                        canFill={canFill}
                        onDirtyChange={handleDirtyChange}
                    />
                </Box>
            </Box>
        </Dialog>
    );
}
